import { masterVendorTypes, vendorSortBy } from 'utils/proptypes';
import PaginateTable from 'components/table/paginate';
import { HiOutlineDuplicate, HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { initMasterVendor } from 'utils/initData';
import SpinnerComponent from 'components/spinner';
import { defaultpageCount, permissionKeys, sortByTypeType } from 'utils/constants';
import { isEqual } from 'lodash';
import NoResult from 'components/commonComponent/noResult';
import { toast } from 'react-toastify';
import { checkPermission, copyToClipboard, formatDateNotTime, messageErrors } from '../../utils/utils';
import { useSelector } from 'react-redux';
import ActionTable from 'components/table/actionTable';
import { orgSelector, userSelector } from 'redux/selectors';
import '../../styles/styles.scss';
import { Table } from 'flowbite-react';
import Sort from 'components/table/sort';
import GroupButtonWithIcons from 'components/button/buttonGroupWithIcons';
import EmptyState from 'components/commonComponent/emptyState';
import ConfirmModal from 'components/modal/confirmModal';
import { deleteMasterVendorById, getMasterVendorsByFilter } from 'api/vendorApi';
import MasterVendorModal from 'components/modal/vendorModal/masterVendorModal';

const vendorEmpty = require('../../assets/image/svg/document-text.svg').default || '';

const MasterVendorList = (props: any) => {
  const queryClient = useQueryClient();
  const { WRITE_VENDOR, WRITE_ORG } = permissionKeys;
  const [t] = useTranslation();
  const { userInfo } = useSelector(userSelector);
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalEntities, setTotalEntities] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const [idDelete, setIdDelete] = useState('');
  const [isEdit, setIsEdit] = useState(false);
  const [vendors, setVendors] = useState([initMasterVendor]);
  const [targetVendor, setTargetVendor] = useState(initMasterVendor);
  const [sortBy, setSortBy] = useState<vendorSortBy | ''>('');
  const [sortByType, setSortByType] = useState('');
  const [pageCount, setPageCount] = useState(defaultpageCount);
  const { organizationId } = useSelector(orgSelector);
  const isWriteVendor = checkPermission(userInfo, props.type, [WRITE_VENDOR, WRITE_ORG], organizationId);

  const handleAddVendor = () => {
    setOpenModal(true);
    setIsEdit(false);
  };

  const fetchVendors = () => {
    return getMasterVendorsByFilter({
      page: currentPage,
      limit: pageCount,
      searchQuery: searchValue,
      sortByType,
      ...(sortBy ? { sortBy } : {}),
    });
  };

  const { data, isLoading, error } = useQuery(
    ['getMasterVendors', currentPage, searchValue, sortBy, sortByType, pageCount],
    fetchVendors,
    {
      staleTime: Infinity,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    if (error) {
      setVendors([initMasterVendor]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleSearch = (value: any) => {
    setSearchValue(value);
    setCurrentPage(0);
  };

  const handleCopyToClipboard = (e: any, item: any, type: string) => {
    switch (type) {
      case 'name':
        copyToClipboard(item.name, t('masterVendorPage.copiedName'));
        break;
      case 'description':
        copyToClipboard(item.description, t('masterVendorPage.copiedDescription'));
        break;
    }
    e.stopPropagation();
  };

  const handleHeaderSort = (field: vendorSortBy) => {
    setSortBy(field);
    let type = '';
    if (field !== sortBy) {
      type = sortByTypeType.ASC;
    } else if (sortByType === sortByTypeType.ASC) {
      type = sortByTypeType.DESC;
    } else {
      setSortBy('');
    }
    setSortByType(type);
  };

  const handleEdit = async (item: any) => {
    if (isWriteVendor) {
      setTargetVendor(item);
      setOpenModal(true);
      setIsEdit(true);
    }
  };

  const handleDelete = async (id: string) => {
    setIdDelete(id);
    setOpenModalConfirm(!openModalConfirm);
  };

  const onDelete = async (id: string) => {
    try {
      const response = await deleteMasterVendorById(id);
      if (response.status === 200) {
        await queryClient.invalidateQueries(['getMasterVendors'], { refetchType: 'active' }, { cancelRefetch: true });
        const message: string = t('masterVendorPage.deleteSuccessfully');
        toast.success(message);
        setOpenModalConfirm(!openModalConfirm);
      }
    } catch (error: any) {
      const message: string = messageErrors(error, t);
      toast.error(message);
    }
  };

  useEffect(() => {
    if (data) {
      setVendors(data?.data?.entities || []);
      setTotalEntities(data.data?.totalEntities || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    setTotalEntities(data?.data?.totalEntities || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  if (isLoading) {
    return null;
  }

  return (
    <>
      {error ? <EmptyState buttonName={t('masterVendorPage.addVendor')}
        handleAddClick={isWriteVendor && handleAddVendor}
        title={t('masterVendorPage.titleNoResult')}
        subtitle={t('masterVendorPage.subTitleNoResult')}
        icon={vendorEmpty}
      /> :
        <>
          {!isEqual(vendors[0], initMasterVendor) && totalEntities === 0 && searchValue === ''
            ?
            <EmptyState buttonName={t('masterVendorPage.addVendor')}
              handleAddClick={isWriteVendor && handleAddVendor}
              title={t('masterVendorPage.titleNoResult')}
              subtitle={t('masterVendorPage.subTitleNoResult')}
              icon={vendorEmpty}
            />
            :
            !isEqual(vendors[0], initMasterVendor) && totalEntities === 0
              ?
              (
                <div className="p-[30px]">
                  <ActionTable
                    placeholderSearch={t('masterVendorPage.searchPlaceholder')}
                    buttonName={t('masterVendorPage.addVendor')}
                    handleAddClick={isWriteVendor && handleAddVendor}
                    handleSearch={handleSearch}
                    searchValue={searchValue}
                  />
                  <NoResult />
                </div>
              )
              :
              (
                <div className="main-sub">
                  <ActionTable
                    placeholderSearch={t('masterVendorPage.searchPlaceholder')}
                    buttonName={t('masterVendorPage.addVendor')}
                    handleAddClick={isWriteVendor && handleAddVendor}
                    handleSearch={handleSearch}
                  />
                  <Table className="h-full overflow-y-scroll">
                    <Table.Head className="text-xs text-gray-900 border-b-2 border-gray-100">
                      <Table.HeadCell className="cursor-pointer bg-white font-medium text-xs px-3 w-30"
                        onClick={() => handleHeaderSort('name')}>
                        <div className="flex items-center ">
                          {t('masterVendorPage.name').toUpperCase()}
                          <Sort check={sortBy === 'name'} sortByType={sortByType} />
                        </div>
                      </Table.HeadCell>
                      <Table.HeadCell
                        className="cursor-pointer hidden-mobile w-30 bg-white font-medium text-xs px-3"
                        onClick={() => {
                          handleHeaderSort('description');
                        }}
                      >
                        <div className="flex items-center">
                          {t('masterVendorPage.description').toUpperCase()}
                          <Sort check={sortBy === 'description'} sortByType={sortByType} />
                        </div>
                      </Table.HeadCell>
                      <Table.HeadCell
                        className="cursor-pointer hidden-mobile w-30 bg-white font-medium text-xs px-3"
                        onClick={() => {
                          handleHeaderSort('created');
                        }}
                      >
                        <div className="flex items-center">
                          {t('masterVendorPage.created').toUpperCase()}
                          <Sort check={sortBy === 'created'} sortByType={sortByType} />
                        </div>
                      </Table.HeadCell>
                      <Table.HeadCell
                        className="cursor-pointer hidden-mobile w-30 bg-white font-medium text-xs px-3"
                        onClick={() => {
                          handleHeaderSort('modified');
                        }}
                      >
                        <div className="flex items-center">
                          {t('masterVendorPage.updated').toUpperCase()}
                          <Sort check={sortBy === 'modified'} sortByType={sortByType} />
                        </div>
                      </Table.HeadCell>
                      <Table.HeadCell className="w-6 bg-white">{''}</Table.HeadCell>
                    </Table.Head>
                    <Table.Body>
                      {vendors.map((item: masterVendorTypes, index) => (
                        <Table.Row key={`row-${index + 1}`}
                          className="bg-white text-sm hover:bg-gray-50 border-b border-gray-100 cursor-pointer">
                          <Table.Cell className="font-semibold py-2.5 px-3">
                            <div className="flex flex-row items-center"
                              onClick={() => handleEdit(item)}>
                              <div className="text-secondary">{item?.name}</div>
                              <div className="copy-icon pl-3 cursor-pointer text-gray-800">
                                <HiOutlineDuplicate size={25}
                                  onClick={(e: any) => handleCopyToClipboard(e, item, 'name')} />
                              </div>
                            </div>
                          </Table.Cell>
                          <Table.Cell className="py-2.5 px-3">
                            <div className="flex flex-row items-center"
                              onClick={() => handleEdit(item)}>
                              <div className="text-black">{item?.description}</div>
                              <div className="copy-icon pl-3 cursor-pointer text-gray-800">
                                <HiOutlineDuplicate size={25}
                                  onClick={(e: any) => handleCopyToClipboard(e, item, 'description')} />
                              </div>
                            </div>
                          </Table.Cell>
                          <Table.Cell className="py-2.5 px-3">
                            <div className="flex flex-row items-center"
                              onClick={() => handleEdit(item)}>
                              <div className="text-black">{formatDateNotTime(item?.created || '')}</div>
                            </div>
                          </Table.Cell>
                          <Table.Cell className="py-2.5 px-3">
                            <div className="flex flex-row items-center"
                              onClick={() => handleEdit(item)}>
                              <div className="text-black">{formatDateNotTime(item?.modified || '')}</div>
                            </div>
                          </Table.Cell>
                          <Table.Cell className="py-2.5">
                            {isWriteVendor && (
                              <GroupButtonWithIcons
                                className="items-center justify-center"
                                buttons={[
                                  {
                                    type: 'button',
                                    text: '',
                                    classType: 'white',
                                    icon: <HiOutlinePencil size={'20px'} />,
                                    action: () => handleEdit(item),
                                  },
                                  {
                                    type: 'submit',
                                    text: '',
                                    classType: 'white',
                                    icon: <HiOutlineTrash size={'20px'} />,
                                    action: () => {
                                      handleDelete(item?.id);
                                    },
                                  },
                                ]}
                              />
                            )}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                  {isLoading && <SpinnerComponent />}
                  <PaginateTable
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    totalEntities={totalEntities}
                    isLoadingTable={isLoading}
                  />
                </div>
              )
          }
        </>}
      {openModal && (
        <MasterVendorModal
          headerTitle={isEdit ? t('masterVendorPage.editVendor') : t('masterVendorPage.addVendor')}
          openModal={openModal}
          isEdit={isEdit}
          targetData={targetVendor}
          setOpenModal={setOpenModal}
          queryClient={queryClient}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
        />
      )}
      {openModalConfirm && (
        <ConfirmModal
          openModal={openModalConfirm}
          setOpenModal={setOpenModalConfirm}
          onDelete={() => onDelete(idDelete)}
        />
      )}
    </>
  );
};

export default MasterVendorList;
